import React from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { useMenuContext } from "Components/MenuProvider";
import { Profile } from "@max/common/user";

interface Props {
  item: SectionItem;
}

type Group = Exclude<Profile["groups"][0], "admin"> | "all";

export type SectionItem = {
  title: string;
  icon: React.ReactNode;
  groups?: Group[];
} & ({ path: string; url?: never } | { url: string; path?: never }) &
  (
    | { comingSoon: true; description?: string }
    | { comingSoon?: false; description: string }
  );

export const Item: React.FC<Props> = ({ item }) => {
  const { path, url, comingSoon } = item;
  const location = useLocation();
  const { menuMode } = useMenuContext();

  return (
    <Container
      {...(comingSoon
        ? { as: "div" }
        : url
        ? { as: "a", href: url, target: "_blank" }
        : { to: path })}
      data-active={location.pathname === path}
      data-menu-mode={menuMode}
      data-coming-soon={comingSoon}
    >
      <Icon>{item.icon}</Icon>
      {menuMode === "expanded" && (
        <Content>
          <Title>{item.title}</Title>
          {(item.description || comingSoon) && (
            <Description>{item.description || "Coming Soon"}</Description>
          )}
        </Content>
      )}
    </Container>
  );
};

const Container = styled(Link)`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 15px;
  padding: 15px 30px;
  margin-right: 15px;
  border-radius: 0 50px 50px 0;
  text-decoration: none;
  color: inherit;

  &[data-active="true"] {
    background-color: black;
    color: white;
  }

  &[data-menu-mode="collapsed"] {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 25px;
    margin: auto;
    width: 30px;
    height: 30px;
    border-radius: 50%;
  }

  &[data-coming-soon="true"] {
    color: var(--tertiary-text-color);
    user-select: none;
  }
`;

const Icon = styled.div`
  svg {
    width: 20px;
    height: 20px;
  }

  [data-menu-mode="collapsed"] & {
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 21px;
      height: 21px;
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Title = styled.span`
  font-size: 16px;
  font-weight: 500;
`;

const Description = styled.span`
  font-size: 12px;
  font-weight: 400;

  [data-active="true"] & {
    color: var(--tertiary-text-color);
  }
`;
